import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowUpRight } from "lucide-react";
import { BOOKING_URL, SERVICE_CATEGORIES } from "../data/content";
import { useT } from "../i18n/LanguageContext";

function ServiceRow({ name, price, idx }) {
  const { tName, tPrice } = useT();
  return (
    <motion.li
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: idx * 0.03 }}
      className="flex items-baseline justify-between gap-4 py-3 border-b border-dashed border-white/10 last:border-b-0"
      data-testid={`service-row-${idx}`}
    >
      <span className="text-white/85 text-sm sm:text-base font-light">{tName(name)}</span>
      <span className="text-[#D4AF37] font-serif-display text-lg sm:text-xl whitespace-nowrap">
        {tPrice(price)}
      </span>
    </motion.li>
  );
}

function CategoryPanel({ category, isOpen, onToggle }) {
  const { t } = useT();
  const images = category.images || [];
  const hasImages = images.length > 0;
  const title = t(`cat.${category.id}.title`);
  const subtitle = t(`cat.${category.id}.subtitle`);
  const badgeLabel = category.badge ? t(`cat.${category.id}.badge`) : null;

  return (
    <div
      className={`card-lux lux-accordion ${isOpen ? "border-[rgba(212,175,55,0.45)]" : ""}`}
      data-state={isOpen ? "open" : "closed"}
      data-testid={`category-${category.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-6 sm:p-8 flex items-center justify-between gap-6 group"
        data-testid={`category-toggle-${category.id}`}
      >
        <div className="flex-1 min-w-0">
          {badgeLabel && (
            <span className="inline-block mb-3 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">
              {badgeLabel}
            </span>
          )}
          <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight">
            {title}
          </h3>
          <p className="mt-2 text-sm text-white/50 tracking-wide">{subtitle}</p>
        </div>
        <div className="flex items-center justify-center w-12 h-12 rounded-full border border-white/15 text-[#D4AF37] flex-shrink-0 transition-all duration-300 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black">
          {isOpen ? <Minus size={18} strokeWidth={1.5} /> : <Plus size={18} strokeWidth={1.5} />}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className={`px-6 sm:px-8 pb-8 ${hasImages ? "grid grid-cols-1 lg:grid-cols-5 gap-8" : ""}`}>
              {hasImages && (
                <div className="lg:col-span-2 hidden lg:flex flex-col gap-4">
                  {images.map((src, i) => (
                    <div
                      key={i}
                      className="relative overflow-hidden flex-1 min-h-[200px]"
                      data-testid={`category-image-${category.id}-${i}`}
                    >
                      <img
                        src={src}
                        alt={title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    </div>
                  ))}
                </div>
              )}
              <div className={hasImages ? "lg:col-span-3" : ""}>
                <ul className="space-y-0">
                  {category.items.map((item, i) => (
                    <ServiceRow key={item.name} {...item} idx={i} />
                  ))}
                </ul>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`category-book-${category.id}`}
                  className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] border-b border-[#D4AF37]/30 pb-1 hover:border-[#D4AF37] transition-colors"
                >
                  {t("services.book_for")} {title} <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Services() {
  const [openId, setOpenId] = useState("angebote");
  const { t } = useT();

  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="relative py-28 sm:py-40 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16 sm:mb-20"
        >
          <span className="overline">{t("services.overline")}</span>
          <h2
            className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] font-light"
            data-testid="services-title"
          >
            {t("services.title_1")}{" "}
            <em className="text-[#D4AF37] not-italic">{t("services.title_em")}</em>{" "}
            {t("services.title_2")}
          </h2>
          <p className="mt-6 text-white/60 text-base sm:text-lg max-w-2xl font-light leading-relaxed">
            {t("services.description")}
          </p>
          <div className="mt-8 ornament justify-start">
            <div className="line" />
            <div className="diamond" />
            <div className="line" />
          </div>
        </motion.div>

        <div className="space-y-4 sm:space-y-5">
          {SERVICE_CATEGORIES.map((cat) => (
            <CategoryPanel
              key={cat.id}
              category={cat}
              isOpen={openId === cat.id}
              onToggle={() => setOpenId(openId === cat.id ? "" : cat.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
